// src/lib/dateUtils.ts

// --- Robust månads-mapping (engelska + svenska) utan dubbletter ---

// Normalisera sträng för månadslookup (små bokstäver, ta bort diakritik & punkter)
const normalizeMonthToken = (s: string): string =>
  s.toLowerCase().replace(/\./g, "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Synonymer per månad
const MONTH_SYNONYMS: Array<[number, string[]]> = [
  [1, ["jan", "january", "januari"]],
  [2, ["feb", "february", "februari"]],
  [3, ["mar", "march", "mars"]],
  [4, ["apr", "april"]],
  [5, ["may", "maj"]],
  [6, ["jun", "june", "juni"]],
  [7, ["jul", "july", "juli"]],
  [8, ["aug", "august", "augusti"]],
  [9, ["sep", "sept", "september"]],
  [10, ["oct", "october", "okt", "oktober"]],
  [11, ["nov", "november"]],
  [12, ["dec", "december", "december"]],
];

// Bygg upp en lookup-tabell (körs när modulen laddas)
const MONTH_NAME_TO_NUM: Record<string, number> = {};
for (const [num, names] of MONTH_SYNONYMS) {
  for (const name of names) {
    MONTH_NAME_TO_NUM[normalizeMonthToken(name)] = num;
  }
}

// Hjälpfunktioner för datumvalidering och formatering
const pad2 = (n: number): string => String(n).padStart(2, "0");
const isValidYMD = (y: number, m: number, d: number): boolean => {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const t = new Date(y, m - 1, d); // Månad är 0-indexerad i Date-objektet
  return t.getFullYear() === y && t.getMonth() === m - 1 && t.getDate() === d;
};

/**
 * Tolkar lösa datumformat (t.ex. "June 15, 2020", "15 juni 2020", "2020-06-15", "15/6/2020")
 * och returnerar ISO "YYYY-MM-DD" eller null om otolkbart.
 */
export function parseLooseDateToISO(raw: string | null | undefined): string | null { // <-- EXPORT TILLAGD HÄR
  if (!raw) return null;

  // Trim + städa: ta bort ordningsändelser (1st/2nd/3rd/4th) och punkter i månadsabbrev (Oct.)
  const s = String(raw).trim() // Säkerställ att det är en sträng
    .replace(/\b(\d{1,2})(st|nd|rd|th)\b/gi, "$1")
    .replace(/\./g, "");

  // Försök matcha olika format i prioriteringsordning

  // 1) Month-name first: "June 15, 2020" / "oktober 15, 2020"
  {
    const m = s.match(/^\s*([A-Za-zÅÄÖåäö]+)\s+(\d{1,2})(?:,)?\s+(\d{4})\s*$/);
    if (m) {
      const monthName = normalizeMonthToken(m[1]);
      const day = parseInt(m[2], 10);
      const year = parseInt(m[3], 10);
      const month = MONTH_NAME_TO_NUM[monthName];
      if (month && isValidYMD(year, month, day)) {
        return `${year}-${pad2(month)}-${pad2(day)}`;
      }
    }
  }

  // 2) Day first: "15 June 2020" / "15 juni 2020"
  {
    const m = s.match(/^\s*(\d{1,2})\s+([A-Za-zÅÄÖåäö]+)\s+(\d{4})\s*$/);
    if (m) {
      const day = parseInt(m[1], 10);
      const monthName = normalizeMonthToken(m[2]);
      const year = parseInt(m[3], 10);
      const month = MONTH_NAME_TO_NUM[monthName];
      if (month && isValidYMD(year, month, day)) {
        return `${year}-${pad2(month)}-${pad2(day)}`;
      }
    }
  }

  // 3) Numeriskt ISO/US: "2020-06-15" / "2020/6/15" / "2020.6.15"
  {
    const m = s.match(/^\s*(\d{4})[.\-/ ](\d{1,2})[.\-/ ](\d{1,2})\s*$/);
    if (m) {
      const year = parseInt(m[1], 10);
      const month = parseInt(m[2], 10);
      const day = parseInt(m[3], 10);
      if (isValidYMD(year, month, day)) {
        return `${year}-${pad2(month)}-${pad2(day)}`;
      }
    }
  }

  // 4) Numeriskt EU: "15-06-2020" / "15/6/2020" / "15.6.2020"
  {
    const m = s.match(/^\s*(\d{1,2})[.\-/ ](\d{1,2})[.\-/ ](\d{4})\s*$/);
    if (m) {
      const day = parseInt(m[1], 10);
      const month = parseInt(m[2], 10);
      const year = parseInt(m[3], 10);
      if (isValidYMD(year, month, day)) {
        return `${year}-${pad2(month)}-${pad2(day)}`;
      }
    }
  }

  // Inget format matchade
  return null;
}